import { CollectionImage } from "@/app/collections/view/collection-image"
import { getCollectionHref } from "@/app/navigation/collection"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import type { CollectionMiniature } from "@anaconda/core/nft-collection/types"
import { Trans } from "@lingui/react/macro"
import Link from "next/link"
import { AppToast, SuccessViewTransaction } from "./app-toast"
import { ToastItemName } from "./toast-item-name"
import { isError, useToastError } from "./toast-util"

export const WhitelistToast = {
  Error: function WhitelistToastError({
    collection,
    id,
    blockchain,
    error,
  }: {
    collection: CollectionMiniature
    id: string | number
    blockchain: Blockchain
    error: unknown
  }) {
    const { name, image } = collection
    const title = getWhitelistText({ collection })
    const main = <WhitelistLink collection={collection} />

    const isAlreadyWhitelistedContractError =
      isError(error) && error.message.includes("is already whitelisted")
    if (isAlreadyWhitelistedContractError) {
      return (
        <AppToast.Error
          id={id}
          title={title}
          image={<CollectionImage collectionName={name} imageSrc={image} />}
          main={main}
          second={
            <div className="font-semibold text-destructive">
              <Trans>This collection is already whitelisted</Trans>
            </div>
          }
        />
      )
    }

    const { message, type } = useToastError(error, blockchain)

    if (type === "destructive") {
      return (
        <AppToast.Error
          id={id}
          title={title}
          image={<CollectionImage collectionName={name} imageSrc={image} />}
          main={main}
          second={
            <div className="font-semibold text-destructive">{message}</div>
          }
        />
      )
    } else if (type === "surface") {
      return (
        <AppToast.Warning
          id={id}
          image={<CollectionImage collectionName={name} imageSrc={image} />}
          title={title}
          main={main}
          second={
            <div className="font-semibold text-muted-foreground">{message}</div>
          }
        />
      )
    }
  },
  Pending: function WhitelistToastPending({
    collection,
    id,
  }: {
    collection: CollectionMiniature
    id: string | number
  }) {
    const { name, image } = collection
    return (
      <AppToast.Pending
        id={id}
        image={<CollectionImage collectionName={name} imageSrc={image} />}
        title={getWhitelistText({ collection })}
        main={<WhitelistLink collection={collection} />}
        second={
          <div className="font-semibold text-muted-foreground">
            <Trans>Pending...</Trans>
          </div>
        }
      />
    )
  },
  Success: function WhitelistToastSuccess({
    id,
    collection,
    transactionId,
  }: {
    collection: CollectionMiniature
    id: string | number
    transactionId: string
  }) {
    const { name, image } = collection
    return (
      <AppToast.Success
        id={id}
        image={<CollectionImage collectionName={name} imageSrc={image} />}
        title={`${name} successfully listed`}
        main={<SuccessWhitelistLink collection={collection} />}
        second={
          <SuccessViewTransaction
            transactionId={transactionId}
            blockchain={collection.blockchain}
          />
        }
      />
    )
  },
}

function SuccessWhitelistLink({
  collection,
}: { collection: CollectionMiniature }) {
  const { name, id } = collection
  const itemHref = getCollectionHref(id)
  return (
    <Trans>
      <Link href={itemHref} className="text-brand">
        <ToastItemName name={name} />
      </Link>{" "}
      successfully listed!
    </Trans>
  )
}

function WhitelistLink({ collection }: { collection: CollectionMiniature }) {
  const { name, id } = collection
  const itemHref = getCollectionHref(id)
  return (
    <Trans>
      Listing{" "}
      <Link href={itemHref} className="text-brand">
        {name}
      </Link>
    </Trans>
  )
}

function getWhitelistText({ collection }: { collection: CollectionMiniature }) {
  const { name } = collection
  const title = `Listing ${name}`
  return title
}
